/**
 * Display rules shared by the USSD pages: environment stages, status words
 * and tones for <app-status>, auth types, the USSD screen-length check and
 * a few formatters. Pure functions, safe on the server.
 */
import { COUNTRIES, CountryInfo, UssdApplication } from '../../../types/ussd.models';
import { describeChar, smsParts } from '../../../../utils/sms';

// ── Environments ─────────────────────────────────────────────

export type UssdEnvironment = 'DEVELOPMENT' | 'SANDBOX' | 'UAT' | 'PRODUCTION';

export const USSD_ENVIRONMENTS: { key: UssdEnvironment; label: string }[] = [
  { key: 'DEVELOPMENT', label: 'Development' },
  { key: 'SANDBOX', label: 'Sandbox' },
  { key: 'UAT', label: 'UAT' },
  { key: 'PRODUCTION', label: 'Production' },
];

export function normaliseEnvironment(value: string | null | undefined): UssdEnvironment {
  const upper = (value || '').toUpperCase();
  return (USSD_ENVIRONMENTS.find((e) => e.key === upper)?.key ?? 'DEVELOPMENT') as UssdEnvironment;
}

export function environmentLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const upper = value.toUpperCase();
  return USSD_ENVIRONMENTS.find((e) => e.key === upper)?.label ?? sentenceCase(value);
}

export function environmentIndex(value: string | null | undefined): number {
  return USSD_ENVIRONMENTS.findIndex((e) => e.key === normaliseEnvironment(value));
}

/** The one stage an app can be promoted to next, or null in production. */
export function nextEnvironment(value: string | null | undefined): UssdEnvironment | null {
  const idx = environmentIndex(value);
  return idx >= 0 && idx < USSD_ENVIRONMENTS.length - 1 ? USSD_ENVIRONMENTS[idx + 1].key : null;
}

// ── Status words and tones ───────────────────────────────────
// <app-status> picks its dot colour from the `status` string, so each value
// here is chosen to land on the right tone: success → ok, pending → wait,
// failed → bad, disabled/cancelled → idle, anything else → busy.

export interface StatusView {
  text: string;
  status: string;
}

export function appStatusView(app: Pick<UssdApplication, 'enabled'> | null | undefined): StatusView {
  return app?.enabled === false
    ? { text: 'Disabled', status: 'disabled' }
    : { text: 'Enabled', status: 'success' };
}

export function promotionStatusView(status: string | null | undefined): StatusView {
  switch ((status || '').toUpperCase()) {
    case 'PROMOTED':
      return { text: 'Promoted', status: 'success' };
    case 'APPROVED':
      return { text: 'Approved', status: 'success' };
    case 'PENDING_APPROVAL':
      return { text: 'Awaiting approval', status: 'pending' };
    case 'REJECTED':
      return { text: 'Rejected', status: 'rejected' };
    case 'ROLLED_BACK':
      return { text: 'Rolled back', status: 'cancelled' };
    case 'CANCELLED':
      return { text: 'Cancelled', status: 'cancelled' };
    case 'DRAFT':
      return { text: 'Draft', status: 'draft' };
    case 'FAILED':
      return { text: 'Failed', status: 'failed' };
    default:
      return { text: sentenceCase(status || 'Unknown'), status: 'cancelled' };
  }
}

export type OutcomeGroup = 'completed' | 'user' | 'failed' | 'open';

/** Usage-record outcomes, grouped the way the breakdown counts them. */
export function outcomeGroup(outcome: string | null | undefined): OutcomeGroup {
  switch ((outcome || '').toUpperCase()) {
    case 'COMPLETED':
      return 'completed';
    case 'TERMINATED_BY_USER':
      return 'user';
    case 'TERMINATED_BY_NETWORK':
    case 'TIMEOUT':
    case 'ERROR':
    case 'SESSION_FAILED':
      return 'failed';
    default:
      return 'open';
  }
}

export function outcomeStatusView(outcome: string | null | undefined): StatusView {
  switch ((outcome || '').toUpperCase()) {
    case 'COMPLETED':
      return { text: 'Completed', status: 'success' };
    case 'TERMINATED_BY_USER':
      return { text: 'Ended by subscriber', status: 'cancelled' };
    case 'TERMINATED_BY_NETWORK':
      return { text: 'Ended by network', status: 'failed' };
    case 'TIMEOUT':
      return { text: 'Timed out', status: 'failed' };
    case 'ERROR':
      return { text: 'Error', status: 'error' };
    case 'SESSION_FAILED':
      return { text: 'Session failed', status: 'failed' };
    case 'PENDING':
      return { text: 'Pending', status: 'pending' };
    case 'ACTIVE':
    case 'STARTED':
      return { text: 'In progress', status: 'active' };
    default:
      return { text: outcome ? sentenceCase(outcome) : 'Unknown', status: 'cancelled' };
  }
}

// ── Callback authentication ──────────────────────────────────

/** The values the USSD gateway understands for callbackAuthType. */
export type UssdAuthType = 'NONE' | 'BEARER' | 'BASIC' | 'API_KEY' | 'CUSTOM';

export const USSD_AUTH_TYPES: { value: UssdAuthType; label: string; hint: string }[] = [
  { value: 'NONE', label: 'None', hint: 'Requests reach your callback without credentials.' },
  { value: 'BEARER', label: 'Bearer token', hint: 'Sent as "Authorization: Bearer <token>".' },
  { value: 'BASIC', label: 'Basic auth', hint: 'A username and password sent as "Authorization: Basic …".' },
  { value: 'API_KEY', label: 'API key', hint: 'A key sent in the header you name.' },
  { value: 'CUSTOM', label: 'Custom header', hint: 'Any header name and value your service expects.' },
];

/**
 * Stored value → gateway value. Apps created by older versions of the portal
 * were saved with BEARER_TOKEN, BASIC_AUTH or OAUTH2, which the gateway does
 * not recognise; the first two map to their real names, OAuth 2.0 has no
 * equivalent and reads as unsupported.
 */
export function normaliseAuthType(raw: string | null | undefined): { type: UssdAuthType; legacy: string | null } {
  const value = (raw || 'NONE').toUpperCase().replace(/[\s-]+/g, '_');
  switch (value) {
    case 'NONE':
    case '':
      return { type: 'NONE', legacy: null };
    case 'BEARER':
      return { type: 'BEARER', legacy: null };
    case 'BEARER_TOKEN':
      return { type: 'BEARER', legacy: null };
    case 'BASIC':
      return { type: 'BASIC', legacy: null };
    case 'BASIC_AUTH':
      return { type: 'BASIC', legacy: null };
    case 'API_KEY':
    case 'APIKEY':
      return { type: 'API_KEY', legacy: null };
    case 'CUSTOM':
      return { type: 'CUSTOM', legacy: null };
    default:
      return { type: 'NONE', legacy: raw || value };
  }
}

export function authLabel(raw: string | null | undefined): string {
  const { type, legacy } = normaliseAuthType(raw);
  if (legacy) return legacy.toUpperCase() === 'OAUTH2' ? 'OAuth 2.0 (not supported)' : `${legacy} (not supported)`;
  return USSD_AUTH_TYPES.find((a) => a.value === type)?.label ?? type;
}

// ── USSD screen check ────────────────────────────────────────

/** A USSD string is at most 160 octets: 182 GSM-7 characters or 80 in UCS-2. */
export const USSD_LIMIT_GSM = 182;
export const USSD_LIMIT_UCS2 = 80;

export interface UssdScreenCheck {
  length: number;
  limit: number;
  encoding: 'GSM-7' | 'UCS-2';
  over: boolean;
  /** Plain words for the character that forced UCS-2, if any. */
  offender: string | null;
  /** "142 of 182 characters, GSM-7" — one line for the transcript. */
  summary: string;
}

export function ussdScreenCheck(text: string | null | undefined): UssdScreenCheck {
  const parts = smsParts(text ?? '');
  const ucs2 = parts.encoding !== 'GSM-7';
  const limit = ucs2 ? USSD_LIMIT_UCS2 : USSD_LIMIT_GSM;
  const length = parts.length;
  const over = length > limit;
  const offender = ucs2 && parts.offender ? describeChar(parts.offender) : null;
  const encoding = ucs2 ? 'UCS-2' : 'GSM-7';
  const summary = `${length} of ${limit} characters, ${encoding}`;
  return { length, limit, encoding, over, offender, summary };
}

// ── Formatters ───────────────────────────────────────────────

export function countryByCode(code: string | null | undefined): CountryInfo | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  return COUNTRIES.find((c) => c.code === upper) ?? null;
}

/** "Botswana" for BW, else whatever name the record carries. */
export function countryName(code: string | null | undefined, fallback?: string | null): string {
  return countryByCode(code)?.name || fallback || code || '—';
}

/** +26771234567 → "+2677•••4567": enough to recognise a test number, not to dial it. */
export function maskMsisdn(msisdn: string | null | undefined): string {
  const value = (msisdn || '').replace(/\s/g, '');
  if (!value) return '—';
  if (value.length <= 7) return value.replace(/.(?=.{2})/g, '•');
  return `${value.slice(0, 5)}•••${value.slice(-4)}`;
}

/** 840 → "840 ms", 4200 → "4.2 s", 125000 → "2 min 5 s". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

export function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** "PENDING_APPROVAL" → "Pending approval". */
export function sentenceCase(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/** NAME → alias: uppercase letters, digits and underscores. */
export function aliasFromName(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** The first message a failed request carries, in words a person can act on. */
export function describeHttpError(err: unknown, fallback: string): string {
  const e = err as { status?: number; error?: unknown } | null;
  const body = e?.error as { detail?: unknown; title?: unknown; message?: unknown } | null | undefined;
  const fromBody = body && typeof body === 'object' ? body.detail || body.title || body.message : null;
  if (typeof fromBody === 'string' && fromBody.trim() && !/^error\./i.test(fromBody.trim())) {
    return `${fallback} ${fromBody.trim().replace(/\.?$/, '.')}`;
  }
  if (e?.status === 0) return `${fallback} The service could not be reached. Check your connection and try again.`;
  if (e?.status === 403) return `${fallback} Your account is not allowed to do this.`;
  if (e?.status === 409) return `${fallback} Another application already uses this name or code.`;
  if (e?.status && e.status >= 500) return `${fallback} The service failed (error ${e.status}). Try again in a moment.`;
  return `${fallback} Try again.`;
}
